import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Phone, Mail, MapPin,
  Flame, ChefHat, Lightbulb, Columns3, Square,
  ShieldCheck, Award, Sparkles, CheckCircle2,
} from "lucide-react";
import { FadeUp, Counter } from "../components/Motion";
import LeadForm from "../components/LeadForm";
import { useSettings } from "../lib/useSettings";
import { FAQSchema, BreadcrumbSchema } from "../components/SeoSchema";
import PageMeta from "../components/PageMeta";

const ELEMENTS = [
  {
    icon: Square,
    kicker: "01 — Foundation",
    title: "Paver Patios & Terraces",
    italic: "The architectural stage.",
    body: "Large-format porcelain, European pavers, or natural flagstone — laid on a 6\" engineered base built for Alberta's freeze-thaw. The patio is where every other outdoor living element lives. We start here, with a foundation that's still flat in 30 years.",
    bullets: ["Techo-Bloc, Belgard & Roman Stone", "Polymeric jointing & edge restraints", "Drainage planning included"],
    image: "https://images.unsplash.com/photo-1780571616416-e44643f9f427?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    icon: Flame,
    kicker: "02 — Warmth",
    title: "Fire Features",
    italic: "From dusk to deep winter.",
    body: "Natural-gas fire tables, masonry fireplaces, and architectural fire pits — engineered for year-round Edmonton evenings. We run gas lines, build CSA-compliant enclosures, and integrate the unit into the patio architecture so it feels deliberate, never bolted-on.",
    bullets: ["NG hard-lines & propane builds", "Cast-stone & cladding integration", "Wind-rated & winter-safe"],
    image: "https://images.unsplash.com/photo-1780719961418-ee8b671e3a46?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    icon: ChefHat,
    kicker: "03 — Hospitality",
    title: "Outdoor Kitchens",
    italic: "Built to host, designed to last.",
    body: "Full outdoor kitchens with built-in grills, side burners, pizza ovens, refrigeration, and natural-stone counters. We frame in CMU block, clad in stone or porcelain, and rough-in gas, power, and water — turning a patio into your favourite room of the house from May to October.",
    bullets: ["Napoleon, Wolf & Lynx built-ins", "Counters: granite · porcelain · concrete", "Gas, water & 120V rough-in"],
    image: "https://images.unsplash.com/photo-1694885090746-d90472e11c0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    icon: Lightbulb,
    kicker: "04 — Atmosphere",
    title: "Landscape Lighting",
    italic: "The detail you only notice at night.",
    body: "Low-voltage architectural lighting designed in layers — path lights, uplit feature trees, wall-grazing on stone, and subtle step lights. A single transformer, brass fixtures, and a smart controller that knows when the sun sets. This is what makes the difference between a yard and a designed landscape.",
    bullets: ["FX Luminaire & Volt fixtures", "Smart timers & dusk-to-dawn", "Brass — built for 20+ winters"],
    image: "https://images.unsplash.com/photo-1780303020973-52dd0b639715?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    icon: Columns3,
    kicker: "05 — Shelter",
    title: "Pergolas, Pavilions & Shade",
    italic: "Architecture for the outdoors.",
    body: "Cedar pergolas, louvered aluminum pavilions with integrated lighting and heaters, and custom timber-frame structures. Engineered to Alberta wind and snow loads, with footings that won't heave. Adjustable roofs let you control sun, rain, and the dinner-party schedule.",
    bullets: ["Cedar, IPE & powder-coated aluminum", "Louvered roofs & motorized screens", "Engineered footings · permit-ready"],
    image: "https://images.unsplash.com/photo-1674672670977-bcf517fc2376?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
];

const PROCESS = [
  { n: "01", title: "Discovery", body: "On-site walk-through. We measure, photograph, and listen to how you want to live in the space." },
  { n: "02", title: "Concept Design", body: "3D renderings, material boards, and lighting plan. You see the project before a shovel breaks ground." },
  { n: "03", title: "Fixed Proposal", body: "A line-by-line proposal with material specs, structural details, and a calendar timeline." },
  { n: "04", title: "Build", body: "Our in-house crew executes — masonry, gas, electrical, carpentry — coordinated by one project lead." },
  { n: "05", title: "Walk-through", body: "Joint commissioning, operation training, and a written care guide. Five-year workmanship warranty." },
];

const FAQS = [
  { q: "What is an Outdoor Living project, exactly?", a: "An Outdoor Living project is a coordinated build that combines paver hardscape, fire features, an outdoor kitchen, architectural lighting, and shade structures into a single, designed space — typically as a flagship backyard renovation. We design and build the entire envelope together, so finishes, gas lines, drainage, and lighting all integrate cleanly." },
  { q: "What does a full Outdoor Living build cost in Edmonton?", a: "Most full Outdoor Living projects fall between $85,000 and $350,000+ in the Edmonton region, depending on patio size, structure complexity, and finish levels. A starter build (large patio + fire table + lighting) often lands in the $50k–$85k range. We provide a fixed proposal after the design phase." },
  { q: "Can you do this in stages?", a: "Yes — many clients build in phases over 2–3 seasons. We design the full master plan first, then execute the patio + structure in year one, the kitchen and lighting in year two, etc. This is the smartest way to spread investment without compromising the final result." },
  { q: "How long does a full build take?", a: "From signed proposal to walk-through, most full Outdoor Living projects run 6–12 weeks of active construction. Design typically takes 4–6 weeks. We schedule April through October, with bookings often filling by February." },
  { q: "Do you handle permits, gas, and electrical?", a: "Yes. Our team includes licensed gas fitters and we coordinate with Master Electricians and structural engineers as required. Permits, inspections, and CSA compliance are part of every Outdoor Living build." },
  { q: "What warranties do you offer?", a: "5-year workmanship warranty on all NorthCrest installations, plus the manufacturer warranties on appliances, fixtures, and pavers (typically 10–25 years). We also offer optional annual maintenance plans." },
];

const HERO_IMG = "https://images.unsplash.com/photo-1762811054950-b74e0a055c80?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const LIFESTYLE_IMG = "https://static.prod-images.emergentagent.com/jobs/1f829ff4-86fc-4a70-85e2-bf7576b5a535/images/d3474410bbbe196014fad00977fb8b3175974ecbfd2b2f5b48929e6941345fbc.png";
const COMMERCIAL_IMG = "https://images.unsplash.com/photo-1780287857844-362c9c040ac4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function OutdoorLiving() {
  const settings = useSettings();
  const phoneDisplay = settings?.phone_display || "(780) 655-7604";
  const phoneE164 = settings?.phone || "+1-780-655-7604";
  const email = settings?.email || "northcrestedmonton@gmail.com";

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="outdoor-living-page" className="bg-[#F5F3EF] text-[#151515]">
      <PageMeta
        title="Outdoor Living Edmonton — Flagship Backyards"
        description="Premium Outdoor Living design-build in Edmonton: paver patios, fire features, outdoor kitchens, landscape lighting, pergolas. Architect-led, fully licensed and insured."
        path="/residential/outdoor-living"
        image="https://images.unsplash.com/photo-1762811054950-b74e0a055c80?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      <FAQSchema faqs={FAQS} />
      <BreadcrumbSchema items={[
        { name: "Home", url: "https://northcrestlandscaping.ca/" },
        { name: "Residential", url: "https://northcrestlandscaping.ca/residential" },
        { name: "Outdoor Living", url: "https://northcrestlandscaping.ca/residential/outdoor-living" },
      ]} />

      {/* HERO — full bleed cinematic */}
      <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden bg-[#0B2B1F]">
        <img
          src={HERO_IMG}
          alt="NorthCrest Outdoor Living — Edmonton backyard with patio, fire feature, and pergola"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2B1F]/95 via-[#0B2B1F]/40 to-[#151515]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2B1F]/60 to-transparent" />

        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-20 md:pb-28 text-[#F5F3EF]">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#D8D6D0]/70 mb-6">
            <Link to="/" className="hover:text-[#E2B85A] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/residential" className="hover:text-[#E2B85A] transition-colors">Residential</Link>
            <span>/</span>
            <span className="text-[#E2B85A]">Outdoor Living</span>
          </div>
          <FadeUp>
            <div className="overline text-[#E2B85A] mb-5">Flagship Service · Edmonton & Region</div>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight max-w-5xl">
              Outdoor <span className="italic text-[#D8D6D0]">Living,</span> built as architecture.
            </h1>
            <p className="text-[#D8D6D0]/85 mt-10 max-w-2xl text-lg md:text-xl font-light leading-relaxed">
              Patios, fire, kitchens, light, and shelter — designed together as one space. A flagship backyard build, engineered for Alberta and finished like a luxury home.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link
                to="/estimate"
                data-testid="outdoor-living-hero-cta"
                className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300"
              >
                Start Your Project <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${phoneE164}`}
                data-testid="outdoor-living-hero-call"
                className="inline-flex items-center justify-center gap-2 px-9 py-4 border border-[#F5F3EF]/30 hover:border-[#F5F3EF] hover:bg-[#F5F3EF] hover:text-[#0B2B1F] text-[#F5F3EF] text-sm tracking-wider uppercase transition-all duration-300"
              >
                <Phone className="w-4 h-4" /> {phoneDisplay}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* INTRO — editorial */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-5">
          <div className="overline text-[#8C1D30] mb-4">What is Outdoor Living</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
            One space. <span className="italic text-[#0B2B1F]">Five disciplines.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-7">
          <p className="text-[#151515]/75 text-lg md:text-xl leading-relaxed">
            Most contractors install a patio, then come back next year for a fire pit, then someone else for lighting. The result feels assembled, never designed. <strong className="text-[#151515]">NorthCrest designs and builds Outdoor Living as a single project</strong> — one architect, one crew, one timeline.
          </p>
          <p className="text-[#151515]/65 mt-6 text-base md:text-lg leading-relaxed">
            We integrate the foundation, the fire, the kitchen, the light, and the shelter so the finished space feels like a continuation of your home — not an add-on. This is our highest-margin service, and our highest-craft.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[#151515]/10 pt-8">
            <div>
              <div className="font-serif-display text-4xl text-[#0B2B1F]"><Counter value={6} suffix="+" /></div>
              <div className="overline text-[10px] text-[#151515]/55 mt-2">Years Edmonton</div>
            </div>
            <div>
              <div className="font-serif-display text-4xl text-[#0B2B1F]"><Counter value={200} suffix="+" /></div>
              <div className="overline text-[10px] text-[#151515]/55 mt-2">Projects Delivered</div>
            </div>
            <div>
              <div className="font-serif-display text-4xl text-[#0B2B1F]">4.9★</div>
              <div className="overline text-[10px] text-[#151515]/55 mt-2">Google Rating</div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* THE FIVE ELEMENTS — alternating editorial blocks */}
      <section className="bg-white border-y border-[#151515]/10">
        <div className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-4">The Five Elements</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight max-w-4xl">
              Every Outdoor Living project is <span className="italic text-[#0B2B1F]">composed of these.</span>
            </h2>
          </FadeUp>
        </div>

        {ELEMENTS.map((el, i) => {
          const reverse = i % 2 === 1;
          const Icon = el.icon;
          return (
            <div
              key={el.title}
              data-testid={`outdoor-living-element-${i}`}
              className={`px-6 md:px-12 lg:px-16 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${i !== 0 ? "border-t border-[#151515]/10" : ""}`}
            >
              <FadeUp className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={el.image} alt={`${el.title} — Edmonton outdoor living`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </FadeUp>
              <FadeUp delay={0.1} className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#0B2B1F] text-[#F5F3EF] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="overline text-[#8C1D30]">{el.kicker}</div>
                </div>
                <h3 className="font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
                  {el.title}
                </h3>
                <div className="font-serif-display italic text-2xl md:text-3xl text-[#0B2B1F]/70 mt-3">{el.italic}</div>
                <p className="text-[#151515]/70 text-base md:text-lg leading-relaxed mt-7">{el.body}</p>
                <ul className="mt-7 space-y-3">
                  {el.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm md:text-base text-[#151515]/80">
                      <CheckCircle2 className="w-4 h-4 mt-1 text-[#8C1D30] flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </FadeUp>
            </div>
          );
        })}
      </section>

      {/* LIFESTYLE FULL-BLEED */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <img src={LIFESTYLE_IMG} alt="NorthCrest Outdoor Living lifestyle — evening backyard" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2B1F]/90 via-[#0B2B1F]/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-16 md:pb-24 text-[#F5F3EF]">
          <FadeUp>
            <div className="overline text-[#E2B85A] mb-4">A new room in your home</div>
            <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-tight max-w-4xl">
              Edmonton has <span className="italic text-[#D8D6D0]">five great outdoor months.</span> Make them count.
            </h2>
          </FadeUp>
        </div>
      </section>

      {/* PROCESS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-[#F5F3EF]">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Our Process</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight max-w-3xl mb-16">
            From first walk-through to <span className="italic text-[#0B2B1F]">final commissioning.</span>
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
          {PROCESS.map((p, i) => (
            <FadeUp key={p.n} delay={i * 0.06}>
              <div className="border-t-2 border-[#0B2B1F] pt-6">
                <div className="font-serif-display text-5xl text-[#8C1D30]">{p.n}</div>
                <h3 className="font-serif-display text-xl mt-4">{p.title}</h3>
                <p className="text-sm text-[#151515]/65 mt-3 leading-relaxed">{p.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="px-6 md:px-12 lg:px-16 py-20 bg-white border-y border-[#151515]/10 grid grid-cols-2 md:grid-cols-4 gap-10">
        <FadeUp>
          <ShieldCheck className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl">Licensed</div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Gas · Electrical · Insured</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <Award className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl"><Counter value={200} suffix="+" /></div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Projects Delivered</div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Sparkles className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl">In-house</div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Masonry · Carpentry · Lighting</div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <CheckCircle2 className="w-7 h-7 text-[#0B2B1F] mb-4" />
          <div className="font-serif-display text-3xl">5-yr</div>
          <div className="overline text-[10px] text-[#151515]/55 mt-2">Workmanship Warranty</div>
        </FadeUp>
      </section>

      {/* COMMERCIAL CROSS-LINK */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-[#F5F3EF]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={COMMERCIAL_IMG} alt="Commercial outdoor space Edmonton" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-6">
            <div className="overline text-[#8C1D30] mb-4">Commercial</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">
              Outdoor Living for restaurants, hotels & multi-residential.
            </h2>
            <p className="text-[#151515]/70 text-base md:text-lg leading-relaxed mb-8">
              We design and build outdoor patios, rooftop terraces, and amenity decks for commercial clients across Edmonton — engineered for high-traffic durability and finished to hospitality-grade standards.
            </p>
            <Link to="/commercial" className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#0B2B1F] text-[#0B2B1F] hover:bg-[#0B2B1F] hover:text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors">
              View Commercial <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-white border-t border-[#151515]/10">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">FAQ</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight max-w-3xl mb-4">
            Outdoor Living <span className="italic text-[#0B2B1F]">questions</span>, answered.
          </h2>
          <p className="text-[#151515]/65 max-w-2xl text-base md:text-lg">
            The most common questions our clients ask before starting a flagship Outdoor Living build.
          </p>
        </FadeUp>
        <div className="mt-12 divide-y divide-[#151515]/10 max-w-4xl">
          {FAQS.map((f, i) => (
            <FadeUp key={f.q} delay={i * 0.04}>
              <details className="group py-6" data-testid={`outdoor-living-faq-${i}`}>
                <summary className="cursor-pointer list-none flex justify-between items-start gap-6">
                  <span className="font-serif-display text-xl md:text-2xl">{f.q}</span>
                  <span className="text-2xl text-[#8C1D30] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#151515]/70 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* LEAD FORM — final CTA */}
      <section id="lead-form" className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-[#0B2B1F] text-[#F5F3EF]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <FadeUp>
            <div className="overline text-[#E2B85A] mb-4">Start Your Outdoor Living Project</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-8">
              Tell us about <span className="italic text-[#D8D6D0]">your backyard.</span>
            </h2>
            <p className="text-[#D8D6D0]/85 mt-4 max-w-md text-base md:text-lg leading-relaxed">
              Share a few details and we&apos;ll set up a complimentary on-site consultation in Edmonton or the surrounding area. Most flagship projects are booked 4–8 weeks in advance — earlier in spring is better.
            </p>
            <div className="mt-10 space-y-3 text-sm">
              <a href={`tel:${phoneE164}`} className="flex items-center gap-3 text-[#D8D6D0]/85 hover:text-[#E2B85A] transition-colors">
                <Phone className="w-4 h-4 text-[#8C1D30]" /> {phoneDisplay}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-[#D8D6D0]/85 hover:text-[#E2B85A] transition-colors">
                <Mail className="w-4 h-4 text-[#8C1D30]" /> {email}
              </a>
              <div className="flex items-center gap-3 text-[#D8D6D0]/85">
                <MapPin className="w-4 h-4 text-[#8C1D30]" /> Edmonton, Alberta — serving the region
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-[#F5F3EF]/15">
              <div className="overline text-[#D8D6D0]/60 mb-3">Or explore</div>
              <div className="flex flex-wrap gap-3">
                <Link to="/yard-designer" className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#F5F3EF]/25 hover:border-[#E2B85A] hover:text-[#E2B85A] text-xs uppercase tracking-widest transition-colors">
                  AI Yard Designer <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/projects" className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#F5F3EF]/25 hover:border-[#E2B85A] hover:text-[#E2B85A] text-xs uppercase tracking-widest transition-colors">
                  See Projects <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <LeadForm leadType="residential" source="outdoor-living" compact />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
